import axios from "axios";

export const questionService = {
  /**
   * Fetch questions with optional filters
   * filters: { subject, gradeLevel, topic, search }
   */
  async fetchQuestions_old(filters = {}) {
    try {
      const params = {};

      if (filters.subject) params.subject_id = String(filters.subject);

      // ✅ Use grade_level_id (more consistent with your other requests)
      if (filters.gradeLevel) params.grade_level_id = String(filters.gradeLevel);

      if (filters.search) params.search = filters.search;

      let url = "/questions";
      if (filters.topic) url = `/topics/${filters.topic}/questions`;

      const response = await axios.get(url, { params });

      // If your API uses Resource pagination: { data: { data: [] } }
      // otherwise: { data: [] }
      return response.data?.data?.data || response.data?.data || response.data;
    } catch (error) {
      console.error("Error fetching questions:", error);
      throw error;
    }
  },

  async fetchQuestions(filters = {}) {
  try {
    const params = {};

    if (filters.search) params.search = filters.search;

    // Use topic route when topic is provided
    if (filters.topic) {
      if (filters.pageSize) params.page_size = String(filters.pageSize);
      if (filters.page) params.page = String(filters.page);

      const response = await axios.get(`/topics/${filters.topic}/questions`, { params });

      // ✅ byTopic() shape: { success, data: [], pagination: {} }
      return {
        items: Array.isArray(response.data?.data) ? response.data.data : [],
        pagination: response.data?.pagination || null,
      };
    }

    // Default /questions route (whatever your controller returns there)
    if (filters.subject) params.subject_id = String(filters.subject);
    if (filters.gradeLevel) params.grade_level_id = String(filters.gradeLevel);

    const response = await axios.get("/questions", { params });

    // Try to support both paginated and non-paginated responses
    const items =
      response.data?.data?.data ||
      response.data?.data ||
      response.data ||
      [];

    return { items: Array.isArray(items) ? items : [], pagination: response.data?.data ? response.data : null };
  } catch (error) {
    console.error("Error fetching questions:", error);
    throw error;
  }
},


  async fetchExistingQuestions(assessmentId) {
    try {
      const response = await axios.get(`/assessments/${assessmentId}/questions`);
      return response.data;
    } catch (error) {
      console.error("Error fetching existing questions:", error);
      throw error;
    }
  },

  async saveQuestions(assessmentId, questionIds, removeIds = []) {
    try {
      const response = await axios.post(`/assessments/${assessmentId}/questions`, {
        question_ids: questionIds,
        remove_ids: removeIds,
      });
      return response.data;
    } catch (error) {
      console.error("Error saving questions:", error);
      throw error;
    }
  },

  async fetchSubjects_old(gradeLevelId) {
    try {
      const url = gradeLevelId ? `/grade-levels/${gradeLevelId}/subjects` : `/subjects`;
      const response = await axios.get(url);
      return response.data;
    } catch (error) {
      console.error("Error fetching subjects:", error);
      throw error;
    }
  },

  async fetchSubjects(gradeId) {
  try {
    const url = gradeId ? `/grade-levels/${gradeId}/subjects` : `/subjects`;
    const response = await axios.get(url);

    const payload = response.data;
    const list =
      payload?.data?.data ||
      payload?.data ||
      payload;

    return Array.isArray(list) ? list : [];
  } catch (error) {
    console.error("Error fetching subjects:", error);
    throw error;
  }
},


  async fetchTopicsWithQuestionsBySubjectAndGrade(subjectId, gradeLevelId) {
    try {
      // NOTE: your endpoint currently ignores gradeLevelId in the URL.
      // Keep as-is unless you have another endpoint that includes grade.
      const response = await axios.get(`/subjects/${subjectId}/topics-with-questions`, {
        params: gradeLevelId ? { grade_level_id: String(gradeLevelId) } : {},
      });
      return response.data;
    } catch (error) {
      console.error("Error fetching topics with questions for subject and grade:", error);
      throw error;
    }
  },

  async fetchTopics_old(subjectId, gradeLevelId) {
    try {
      let url;

      // Your backend uses grades/{gradeId}/units
      if (subjectId && gradeLevelId) {
        url = `/subjects/${subjectId}/grades/${gradeLevelId}/units`;
      } else if (subjectId) {
        url = `/subjects/${subjectId}/topics`;
      } else {
        url = `/topics`;
      }

      const response = await axios.get(url);
      return response.data;
    } catch (error) {
      console.error("Error fetching topics:", error);
      throw error;
    }
  },
  async fetchTopics(subjectId, gradeId) {
  try {
    let url;
    if (subjectId && gradeId) {
      url = `/subjects/${subjectId}/grades/${gradeId}/units`;
    } else if (subjectId) {
      url = `/subjects/${subjectId}/topics`;
    } else {
      url = `/topics`;
    }

    const response = await axios.get(url);

    // ✅ normalize: support {data: []}, {success:true,data:[]}, or plain []
    const payload = response.data;
    const list =
      payload?.data?.data ||  // paginated laravel resource
      payload?.data ||       // {success:true,data:[]}
      payload;               // plain []

    return Array.isArray(list) ? list : [];
  } catch (error) {
    console.error("Error fetching topics:", error);
    throw error;
  }
},


  async fetchGradeLevels() {
    try {
      const response = await axios.get(`/grade-levels`);
      return response.data;
    } catch (error) {
      console.error("Error fetching grade levels:", error);
      throw error;
    }
  },

  async fetchTopicsWithQuestionsBySubject(subjectId) {
    try {
      const response = await axios.get(`/subjects/${subjectId}/topics-with-questions`);
      return response.data;
    } catch (error) {
      console.error("Error fetching topics with questions:", error);
      throw error;
    }
  },

  async searchSubjects(searchTerm) {
    try {
      const response = await axios.get(`/subjects/search`, {
        params: { search: searchTerm },
      });
      return response.data;
    } catch (error) {
      console.error("Error searching subjects:", error);
      throw error;
    }
  },

  // ✅ NEW: Create one question (FormData)
  async createQuestion(formData) {
    try {
      const response = await axios.post("/questions", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      return response.data;
    } catch (error) {
      console.error("Error creating question:", error);
      throw error;
    }
  },

  // ✅ NEW: Update one question (FormData)
  async updateQuestion(questionId, formData) {
    try {
      // Laravel PUT with multipart => use POST + _method=PUT (you already do this elsewhere)
      formData.append("_method", "PUT");
      const response = await axios.post(`/questions/${questionId}`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      return response.data;
    } catch (error) {
      console.error("Error updating question:", error);
      throw error;
    }
  },
};
